import { useEffect } from 'react';
import { redirect, useLocation, type MiddlewareFunction } from 'react-router';
import { persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import {
  completeBranchPath,
  pathnameToBranchPath,
  Tree,
  writeBranchPath,
  INDEX_LEAF,
} from './tree';

type HistoryTree = Array<Tree>;

type Data = {
  tree: HistoryTree;
};

type Actions = {
  complete: (pathnamePrefix: string) => string | null;
  write: (pathnameFull: string, index?: boolean) => void;
  reset: () => void;
};

const store = createStore<Data & Actions>()(
  persist(
    (set, get) => ({
      tree: [],

      complete: (pathnamePrefix) => {
        const branchPath = pathnameToBranchPath(pathnamePrefix);
        let completed = completeBranchPath(get().tree, branchPath);

        if (completed) {
          if (completed.at(-1) === INDEX_LEAF) {
            completed = completed.slice(0, -1);
          }

          return `/${completed.join('/')}`;
        } else {
          return null;
        }
      },

      write: (pathnameFull, index = false) => {
        const branchPath = pathnameToBranchPath(pathnameFull);
        if (index) {
          branchPath.push(INDEX_LEAF);
        }

        set(({ tree: history, ...state }) => ({
          ...state,
          tree: writeBranchPath(history, branchPath),
        }));
      },

      reset: () => {
        set({ tree: [] });
      },
    }),
    {
      name: `history-tree`,
    },
  ),
);

export const { getState: getHistoryTree } = store;

export const useHistoryTreeWrite = (index = false) => {
  const pathname = useLocation().pathname;

  useEffect(() => {
    getHistoryTree().write(pathname, index);
  }, [pathname, index]);
};

export const completePrefixPathMw: MiddlewareFunction = (ctx, next) => {
  const navigated = new URL(ctx.request.url);

  // console.log({ navigated });

  if (navigated.searchParams.has('prefix') || navigated.pathname === '/') {
    const completed = getHistoryTree().complete(navigated.pathname);

    // console.log({ completed });

    if (completed && navigated.pathname !== completed) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect(completed);
    }
  }

  return next();
};
